import React, { useState } from 'react';
import Icon from '../Icon';
import { inputCls, primaryBtn, ghostBtn, Field } from './ui';
import { useHealthConsent, HealthConsentCheck } from './HealthConsent';

const FORMS = ['tablet', 'capsule', 'liquid', 'inhaler', 'injection', 'cream', 'other'];
const FREQUENCIES = [
  { v: 'once_daily', l: 'Once a day' },
  { v: 'twice_daily', l: 'Twice a day' },
  { v: 'three_times_daily', l: 'Three times a day' },
  { v: 'four_times_daily', l: 'Four times a day' },
  { v: 'every_other_day', l: 'Every other day' },
  { v: 'weekly', l: 'Once a week' },
  { v: 'as_needed', l: 'Only when needed' },
  { v: 'other', l: 'Something else' },
];
const STATUSES = [
  { v: 'ongoing', l: 'Ongoing' },
  { v: 'paused', l: 'Paused' },
  { v: 'completed', l: 'Completed' },
  { v: 'discontinued', l: 'Discontinued' },
];
const TIMINGS = ['Morning', 'Midday', 'Evening', 'Bedtime', 'With food', 'Before food', 'After food'];

const empty = {
  name: '', dosage: '', form: 'tablet', frequency: 'once_daily', timing: '',
  start_date: new Date().toISOString().slice(0, 10), status: 'ongoing',
  end_date: '', prescriber: '', reason: '', instructions: '', notes: '',
  reminder_times: [], reminders_enabled: true, notification_preference: 'in_app', snooze_minutes: 10,
};

export default function MedicationForm({ initial, onSave, onCancel, busy, error }) {
  const consent = useHealthConsent();
  const [form, setForm] = useState({ ...empty, ...(initial || {}) });
  const [section, setSection] = useState(1);
  const [newTime, setNewTime] = useState('08:00');

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addTime = () => {
    if (!newTime || form.reminder_times.includes(newTime)) return;
    setForm((f) => ({ ...f, reminder_times: [...f.reminder_times, newTime].sort() }));
  };
  const removeTime = (t) => setForm((f) => ({ ...f, reminder_times: f.reminder_times.filter((x) => x !== t) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!(await consent.ensure())) return;
    const payload = { ...form };
    ['end_date', 'prescriber', 'reason', 'instructions', 'notes', 'timing'].forEach((k) => {
      if (payload[k] === '') payload[k] = null;
    });
    payload.snooze_minutes = Number(payload.snooze_minutes) || 10;
    onSave(payload);
  };

  const SECTIONS = [
    { n: 1, l: 'The medicine' },
    { n: 2, l: 'When you take it' },
    { n: 3, l: 'Reminders' },
    { n: 4, l: 'Extra details' },
  ];

  return (
    <form onSubmit={submit} className="p-space-lg rounded-xl bg-surface-container-low border border-surface-container ph-no-capture" data-testid="medication-form">
      <div className="flex flex-wrap items-center gap-space-xs mb-space-lg">
        {SECTIONS.map((s) => (
          <button
            key={s.n}
            type="button"
            data-testid={`med-form-section-${s.n}`}
            onClick={() => setSection(s.n)}
            className={`h-9 px-space-md rounded-full font-label-sm text-label-sm uppercase tracking-wider transition-colors ${section === s.n ? 'bg-outline text-on-primary' : 'bg-surface-container text-on-surface-variant hover:bg-surface-container-high'}`}
          >
            {s.n}. {s.l}
          </button>
        ))}
      </div>

      {section === 1 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-md">
          <Field label="Medication name *" htmlFor="m-name">
            <input id="m-name" data-testid="med-name" required className={inputCls} value={form.name} onChange={set('name')} placeholder="As written on the label" />
          </Field>
          <Field label="Dosage *" hint="Exactly as prescribed, e.g. 500 mg or 2 puffs." htmlFor="m-dosage">
            <input id="m-dosage" data-testid="med-dosage" required className={inputCls} value={form.dosage} onChange={set('dosage')} placeholder="e.g. 500 mg" />
          </Field>
          <Field label="Form *" htmlFor="m-form">
            <select id="m-form" data-testid="med-form-type" className={inputCls} value={form.form} onChange={set('form')}>
              {FORMS.map((f) => <option key={f} value={f}>{f[0].toUpperCase() + f.slice(1)}</option>)}
            </select>
          </Field>
          <Field label="Status *" hint="You decide this — we never change it for you." htmlFor="m-status">
            <select id="m-status" data-testid="med-status" className={inputCls} value={form.status} onChange={set('status')}>
              {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </Field>
        </div>
      )}

      {section === 2 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-md">
          <Field label="How often *" htmlFor="m-freq">
            <select id="m-freq" data-testid="med-frequency" className={inputCls} value={form.frequency} onChange={set('frequency')}>
              {FREQUENCIES.map((f) => <option key={f.v} value={f.v}>{f.l}</option>)}
            </select>
          </Field>
          <Field label="Timing" hint="Optional — when in the day it fits." htmlFor="m-timing">
            <input id="m-timing" data-testid="med-timing" list="timing-options" className={inputCls} value={form.timing || ''} onChange={set('timing')} placeholder="e.g. Morning, with food" />
            <datalist id="timing-options">{TIMINGS.map((t) => <option key={t} value={t} />)}</datalist>
          </Field>
          <Field label="Start date *" htmlFor="m-start">
            <input id="m-start" data-testid="med-start-date" type="date" required className={inputCls} value={form.start_date || ''} onChange={set('start_date')} />
          </Field>
          <Field label="End date" hint="Leave empty if it's ongoing." htmlFor="m-end">
            <input id="m-end" data-testid="med-end-date" type="date" className={inputCls} value={form.end_date || ''} onChange={set('end_date')} />
          </Field>
        </div>
      )}

      {section === 3 && (
        <div className="flex flex-col gap-space-md">
          <div className="flex flex-wrap items-end gap-space-md">
            <Field label="Add a reminder time" htmlFor="m-time">
              <div className="flex items-center gap-space-xs">
                <input id="m-time" data-testid="med-time-input" type="time" className={inputCls} value={newTime} onChange={(e) => setNewTime(e.target.value)} />
                <button type="button" data-testid="med-add-time" onClick={addTime} className={ghostBtn}>Add</button>
              </div>
            </Field>
            <Field label="Reminders" htmlFor="m-notif">
              <select id="m-notif" data-testid="med-notification" className={inputCls} value={form.notification_preference} onChange={set('notification_preference')}>
                <option value="in_app">Show in my dashboard</option>
                <option value="browser">Browser notification</option>
                <option value="off">No reminders</option>
              </select>
            </Field>
            <Field label="Snooze for (minutes)" htmlFor="m-snooze">
              <input id="m-snooze" data-testid="med-snooze" type="number" min="1" max="240" className={inputCls} value={form.snooze_minutes} onChange={set('snooze_minutes')} />
            </Field>
          </div>
          <div className="flex flex-wrap items-center gap-space-xs" data-testid="med-times-list">
            {form.reminder_times.length === 0 ? (
              <span className="font-body-sm text-body-sm text-on-surface-variant">No times added yet — that’s fine, you can still record doses by hand.</span>
            ) : form.reminder_times.map((t) => (
              <span key={t} className="inline-flex items-center gap-1 px-space-md h-9 rounded-full bg-primary-container text-outline font-label-md text-label-md">
                {t}
                <button type="button" aria-label={`Remove ${t}`} data-testid={`med-remove-time-${t}`} onClick={() => removeTime(t)} className="text-on-surface-variant hover:text-error">
                  <Icon name="close" className="text-[16px]" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {section === 4 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-md">
          <Field label="Prescribed by" htmlFor="m-doc">
            <input id="m-doc" data-testid="med-prescriber" className={inputCls} value={form.prescriber || ''} onChange={set('prescriber')} placeholder="Optional" />
          </Field>
          <Field label="What it's for" htmlFor="m-reason">
            <input id="m-reason" data-testid="med-reason" className={inputCls} value={form.reason || ''} onChange={set('reason')} placeholder="Optional" />
          </Field>
          <Field label="Special instructions" htmlFor="m-inst">
            <input id="m-inst" data-testid="med-instructions" className={inputCls} value={form.instructions || ''} onChange={set('instructions')} placeholder="e.g. Take with a full glass of water" />
          </Field>
          <Field label="Notes" htmlFor="m-notes">
            <textarea id="m-notes" data-testid="med-notes" rows={3} className={`${inputCls} h-auto py-space-sm resize-none`} value={form.notes || ''} onChange={set('notes')} placeholder="Optional" />
          </Field>
        </div>
      )}

      {error ? (
        <p data-testid="med-form-error" className="mt-space-md px-space-md py-space-sm rounded-lg bg-error-container text-on-error-container font-body-sm text-body-sm">{error}</p>
      ) : null}

      <HealthConsentCheck granted={consent.granted} ticked={consent.ticked} setTicked={consent.setTicked} />

      <div className="flex flex-wrap items-center gap-space-md mt-space-lg pt-space-lg border-t border-surface-container">
        <button type="submit" data-testid="med-save" disabled={busy || consent.blocked} className={primaryBtn}>
          <Icon name="check" className="text-[18px]" />
          <span>{busy ? 'Saving…' : initial ? 'Save changes' : 'Add medication'}</span>
        </button>
        <button type="button" data-testid="med-cancel" onClick={onCancel} className={ghostBtn}>Cancel</button>
        {section < 4 && (
          <button type="button" data-testid="med-next-section" onClick={() => setSection(section + 1)} className="font-label-md text-label-md uppercase tracking-wider text-outline hover:text-surface-tint transition-colors">
            Next: {SECTIONS[section].l} →
          </button>
        )}
      </div>
    </form>
  );
}

export { FREQUENCIES, STATUSES };
